import { useState } from "react"
import { Button, Typography } from "src/blitz"
import styles from "./GovernmentBanner.module.scss"
import Logo from "src/images/logo-red-large.svg?react"
import { discoverIdentityProvider } from "src/shared/lib/auth/idpDiscovery"

export const GOV_BANNER_TITLE = "Command Center"
export const GOV_BANNER_WARNING_TITLE = "WARNING"
export const GOV_BANNER_PARAGRAPHS = [
  "You are accessing a Trane Technologies information system that is provided for authorized use only. This system processes information on behalf of Trane Technologies customers, including U.S. Government agencies.",
  "Unauthorized or improper use of this system is prohibited and may result in disciplinary action, termination of access privileges, and civil or criminal penalties.",
  "By using this system, you acknowledge and consent to the monitoring, recording, and auditing of system activities for security, operational, compliance, and other authorized purposes.",
  "Information on this system may be accessed, retained, used, or disclosed by Trane Technologies and authorized government entities, as applicable, in accordance with law, customer agreements, and company policies.",
  "If you do not agree to these conditions, do not access this system and log off immediately."
]

export const EMAIL_REQUIRED_ERROR = "Email address is required."
export const EMAIL_INVALID_ERROR = "Please enter a valid email address."

// RFC 5322 compliant enough pattern for standard email validation.
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

export const validateEmail = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return EMAIL_REQUIRED_ERROR
  if (!EMAIL_REGEX.test(trimmed)) return EMAIL_INVALID_ERROR
  return ""
}

type GovernmentBannerProps = {
  onAcknowledge: (email: string, idpProviderName: string | null) => void
}

const GovernmentBanner = ({ onAcknowledge }: GovernmentBannerProps) => {
  const [email, setEmail] = useState("")
  const [error, setError] = useState("")
  const [isResolvingIdp, setIsResolvingIdp] = useState(false)

  const handleSubmit = async () => {
    const validationError = validateEmail(email)
    if (validationError) {
      setError(validationError)
      return
    }

    const trimmedEmail = email.trim()
    setIsResolvingIdp(true)
    try {
      const discovery = await discoverIdentityProvider(trimmedEmail)
      // On ROUTE_UNAVAILABLE we fall back without surfacing a domain-specific
      // error — avoids leaking which email domains are registered.
      const idpProviderName =
        discovery.result === "ROUTE_AVAILABLE" ? discovery.idpProviderName : null
      onAcknowledge(trimmedEmail, idpProviderName)
    } finally {
      setIsResolvingIdp(false)
    }
  }

  return (
    <div className={styles.govBannerOverlay}>
      <div className={styles.govBannerContent}>
      <h1 className={styles.govBannerTitle}>{GOV_BANNER_TITLE}</h1>
      <Logo className={styles.govBannerLogo} />
      <h2 className={styles.govBannerWarningTitle}>
        {GOV_BANNER_WARNING_TITLE}
      </h2>
      <div data-testid="gov-banner-text">
        {GOV_BANNER_PARAGRAPHS.map((paragraph) => (
          <Typography
            tagType="p"
            key={paragraph}
            className={styles.govBannerParagraph}
          >
            {paragraph}
          </Typography>
        ))}
      </div>
        <div className={styles.govBannerField}>
          <label htmlFor="gov-banner-email" className={styles.govBannerLabel}>
            Email<span aria-hidden="true"> *</span>
          </label>
          <input
            id="gov-banner-email"
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            className={styles.govBannerInput}
            value={email}
            aria-required="true"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "gov-banner-email-error" : undefined}
            onChange={(e) => {
              setEmail(e.target.value)
              if (error) setError(validateEmail(e.target.value))
            }}
            onBlur={() => setError(validateEmail(email))}
            data-testid="gov-banner-email-input"
          />
          {error ? (
            <span
              id="gov-banner-email-error"
              role="alert"
              className={styles.govBannerError}
              data-testid="gov-banner-email-error"
            >
              {error}
            </span>
          ) : null}
        </div>
        <div className={styles.govBannerButtonRow}>
          <Button
            type="button"
            variant="green"
            btnSize="large"
            style={{
              padding: "10px 30px",
              fontSize: "14px",
              textDecoration: "none"
            }}
            onClick={handleSubmit}
            disabled={Boolean(validateEmail(email)) || isResolvingIdp}
            data-testid="gov-banner-acknowledge-btn"
          >
            {isResolvingIdp ? "Signing in..." : "Sign In"}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default GovernmentBanner
